/**
 * Email API Service - aegis5 Email System
 *
 * Provides typed API client for:
 * - Email accounts (IMAP, Google OAuth)
 * - Folders and threads
 * - Message retrieval and actions
 * - Email sending
 * - Sync status
 */

import apiClient from './api';

// ==================== Types ====================

export interface EmailAccount {
  id: string;
  email: string;
  display_name?: string;
  provider: 'imap' | 'google' | 'microsoft';
  sync_status?: 'active' | 'error' | 'syncing' | 'disabled';
  is_active?: boolean;
  imap_host?: string;
  imap_port?: number;
  smtp_host?: string;
  smtp_port?: number;
  last_sync_at?: string;
  last_error?: string;
  folder_count?: number;
  message_count?: number;
  is_primary?: boolean;
  is_preferred?: boolean;  // Alias for is_primary
  created_at: string;
  updated_at?: string;
}

export interface EmailFolder {
  id: string;
  account_id: string;
  name: string;
  folder_type: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | 'starred' | 'important' | 'all' | 'custom';
  path: string;
  unread_count: number;
  total_count: number;
  account_email?: string;
  parent_id?: string;
  children?: EmailFolder[];
}

export interface EmailThreadPreview {
  id: string;
  subject: string;
  snippet: string;
  // Backend returns sender_name/sender_email (aliased from last_sender_name/last_sender_email)
  sender_name?: string;
  sender_email?: string;
  // Legacy field names for compatibility
  from_name?: string;
  from_email?: string;
  participant_count?: number;
  message_count: number;
  unread_count: number;
  has_attachments: boolean;
  is_read?: boolean;
  is_starred: boolean;
  is_important?: boolean;
  labels?: string[];
  last_message_at: string;
  timestamp?: string; // aliased from last_message_at
  participants?: Array<{name?: string; email: string}>;
  ai_summary?: {
    summary: string;
    one_liner?: string;
    category: string;
    priority_score: number;
    requires_response: boolean;
    sentiment?: 'positive' | 'negative' | 'neutral';
    has_action_items?: boolean;
    is_meeting_request?: boolean;
  };
}

export interface EmailMessage {
  id: string;
  thread_id: string;
  subject: string;
  from_name: string;
  from_email: string;
  to: { name: string; email: string }[];
  cc: { name: string; email: string }[];
  bcc: { name: string; email: string }[];
  body_html?: string;
  body_text?: string;
  snippet?: string;
  is_read: boolean;
  is_starred: boolean;
  received_at: string;
  attachments: EmailAttachment[];
  ai_analysis?: {
    summary: string;
    key_points: string[];
    category: string;
    sentiment: string;
    priority_score: number;
    action_items: { action: string; deadline?: string; priority: string }[];
    anomaly_detected: boolean;
    anomaly_details?: {
      type: string;
      severity: string;
      description: string;
    };
  };
}

export interface EmailAttachment {
  id: string;
  filename: string;
  content_type: string;
  mime_type?: string;
  size_bytes: number | null;
  s3_key: string | null;
  is_inline: boolean;
  download_url?: string;
}

export interface ThreadListResponse {
  threads: EmailThreadPreview[];
  total_count: number;
  total_pages: number;
  page: number;
  limit: number;
  folder_id?: string;
  account_id?: string;
}

export interface FolderSummary {
  folders: EmailFolder[];
  total_unread: number;
  accounts: { id: string; email: string; unread: number }[];
}

export interface SendEmailRequest {
  account_id: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body_html?: string;
  body_text?: string;
  reply_to_message_id?: string;
  forward_message_id?: string;
  attachments?: { filename: string; content: string; content_type: string }[];
}

export interface SyncStatus {
  account_id: string;
  status: 'idle' | 'syncing' | 'error';
  progress?: {
    current: number;
    total: number;
    folder: string;
  };
  last_sync_at?: string;
  next_sync_at?: string;
  error_message?: string;
}

// ==================== Intelligence Types ====================

export interface ThreadIntelligenceEntity {
  name: string;
  type?: string;  // Optional, might be 'PERSON' or 'ORGANIZATION'
  entity_type?: string;  // API uses this field
  email_count?: number;
  mention_count?: number;  // API uses this field
  contact_count?: number;
  cogito_id?: string;
  contact_id?: string;
  role?: string;
  email?: string;
}

export interface ThreadIntelligenceDate {
  text: string;
  parsed?: string;
  context?: string;
  is_past?: boolean;
}

export interface ThreadIntelligenceAmount {
  value: number;
  formatted: string;
  currency?: string;
  context?: string;
}

export interface ActionItem {
  id?: string;
  text: string;  // API returns 'text' field
  action?: string;  // Alias for compatibility
  assignee?: string;
  due_date?: string;  // API returns 'due_date' not 'deadline'
  deadline?: string;  // Alias for compatibility
  priority?: 'low' | 'medium' | 'high' | 'urgent' | string;
  status: 'pending' | 'completed' | 'dismissed';
}

export interface ThreadIntelligence {
  thread_id: string;
  message_count?: number;
  first_message_at?: string;
  last_message_at?: string;
  // API returns flat arrays, not nested under 'entities'
  people: ThreadIntelligenceEntity[];
  organizations: ThreadIntelligenceEntity[];
  dates: ThreadIntelligenceDate[];
  amounts: ThreadIntelligenceAmount[];
  // Legacy nested structure for compatibility
  entities?: {
    people: ThreadIntelligenceEntity[];
    organizations: ThreadIntelligenceEntity[];
    dates: ThreadIntelligenceDate[];
    amounts: ThreadIntelligenceAmount[];
  };
  action_items: ActionItem[];
  summary?: {
    context?: string;
    key_ask?: string;
    progress?: string;
    overall_sentiment?: string;
  };
  prognosis?: {
    outcome_prediction?: string;
    confidence_score?: number;
    timeline_risk?: string;
    next_suggested_action?: string;
  };
  overall_sentiment?: string;
  avg_priority?: number;
  requires_response?: boolean;
  participants?: string[];
}

export interface RelatedThread {
  id: string;
  subject: string;
  last_message_at: string;
  message_count: number;
  overlap_type: 'participants' | 'topic' | 'reply_chain';
  overlap_details?: string;
}

export interface RelatedCalendarEvent {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  has_conflict?: boolean;
  conflict_details?: string;
}

export interface RelatedDocument {
  id: string;
  title: string;
  file_type: string;
  relevance_score?: number;
  snippet?: string;
}

export interface CogitoInsight {
  entity_id: string;
  entity_name: string;
  entity_type: string;
  insight: string;
  source?: string;
}

export interface RelatedContext {
  thread_id: string;
  related_threads: RelatedThread[];
  calendar_events: RelatedCalendarEvent[];
  documents: RelatedDocument[];
  cogito_insights: CogitoInsight[];
}

export interface EntityAnnotation {
  start: number;
  end: number;
  text: string;
  entity_type: string;
  entity_id?: string;
}

export interface AnnotatedMessage {
  message_id: string;
  original_content: string;  // HTML or plain text depending on is_html
  annotations: EntityAnnotation[];
  is_html: boolean;  // If true, original_content is HTML and annotations use text-matching (start=-1)
}

export interface DraftImprovement {
  id: string;
  original: string;
  suggested: string;
  reasoning: string;
  type: 'tone' | 'clarity' | 'grammar' | 'structure' | 'content';
  accepted?: boolean;
}

export interface AutomatedAction {
  id: string;
  type: 'calendar' | 'attach_file' | 'follow_up' | 'priority' | 'read_receipt';
  title: string;
  description?: string;
  enabled: boolean;
  // Calendar-specific
  datetime?: string;
  duration_minutes?: number;
  // File-specific
  file_id?: string;
  filename?: string;
  // Follow-up specific
  reminder_at?: string;
}

export interface DraftImprovementResponse {
  draft_id: string;
  improvements: DraftImprovement[];
  automated_actions: AutomatedAction[];
  context_awareness: string[];
  model_used?: string;
}

export interface ExecuteDraftActionsRequest {
  apply_improvements: string[];
  execute_actions: string[];
}

export interface ExecuteDraftActionsResponse {
  success: boolean;
  applied_improvements: string[];
  executed_actions: string[];
  updated_draft_content?: string;
  errors?: string[];
}

export interface CreateAccountRequest {
  email: string;
  display_name?: string;
  provider: 'imap' | 'google';
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  use_ssl?: boolean;
}

export interface TestCredentialsRequest {
  email: string;
  imap_host: string;
  imap_port: number;
  imap_username?: string;
  imap_password: string;
  use_ssl: boolean;
}

export interface TestCredentialsResponse {
  success: boolean;
  message: string;
}

// ==================== API Functions ====================

/**
 * Account Management
 */
export const accountApi = {
  /** List all email accounts */
  list: async (): Promise<EmailAccount[]> => {
    const response = await apiClient.get('/email/accounts');
    // Backend returns array directly, not wrapped in {accounts: [...]}
    return Array.isArray(response.data) ? response.data : response.data.accounts || [];
  },

  /** Create IMAP email account */
  create: async (data: CreateAccountRequest): Promise<EmailAccount> => {
    const response = await apiClient.post('/email/accounts', data);
    return response.data;
  },

  /** Delete email account */
  delete: async (accountId: string): Promise<void> => {
    await apiClient.delete(`/email/accounts/${accountId}`);
  },

  /** Set preferred account */
  setPreferred: async (accountId: string): Promise<void> => {
    await apiClient.post(`/email/accounts/${accountId}/preferred`);
  },

  /** Import Google OAuth tokens */
  importGoogleOAuth: async (
    email: string,
    accessToken: string,
    refreshToken: string,
    expiresAt: string
  ): Promise<EmailAccount> => {
    const response = await apiClient.post('/email/accounts/google-oauth', {
      email,
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: expiresAt,
    });
    return response.data;
  },

  /** Test IMAP credentials before creating account */
  testCredentials: async (data: TestCredentialsRequest): Promise<TestCredentialsResponse> => {
    const response = await apiClient.post('/email/accounts/test-credentials', data);
    return response.data;
  },
};

/**
 * Folders & Threads
 */
export const folderApi = {
  /** Get folder summary with unread counts */
  getSummary: async (accountId?: string): Promise<FolderSummary> => {
    const params = accountId ? { account_id: accountId } : {};
    const response = await apiClient.get('/email/folders/summary', { params });
    return response.data;
  },

  /** List folders for account */
  list: async (accountId: string): Promise<EmailFolder[]> => {
    const response = await apiClient.get('/email/folders', {
      params: { account_id: accountId },
    });
    // Map backend field names to frontend interface
    const folders = response.data.folders || [];
    return folders.map((f: Record<string, unknown>) => ({
      id: f.folder_id || f.id,
      account_id: f.account_id || accountId,
      name: f.folder_name || f.name,
      folder_type: f.folder_type || 'custom',
      path: f.path || f.folder_name || f.name || '',
      unread_count: f.unread_count || 0,
      total_count: f.total_count || 0,
      parent_id: f.parent_id,
      children: f.children,
    }));
  },
};

/**
 * Thread & Message Operations
 */
export const threadApi = {
  /** List threads with pagination */
  list: async (params: {
    account_id?: string;
    folder_id?: string;
    page?: number;
    limit?: number;
    search?: string;
    is_unread?: boolean;
    is_starred?: boolean;
    filter?: 'all' | 'unread' | 'actionable';
  }): Promise<ThreadListResponse> => {
    const response = await apiClient.get('/email/threads', { params });
    return response.data;
  },

  /** Get single thread with messages */
  get: async (threadId: string): Promise<{
    thread: EmailThreadPreview;
    messages: EmailMessage[];
  }> => {
    const response = await apiClient.get(`/email/threads/${threadId}`);
    return response.data;
  },

  /** Mark thread as read */
  markRead: async (threadId: string): Promise<void> => {
    await apiClient.post(`/email/threads/${threadId}/read`);
  },

  /** Mark thread as unread */
  markUnread: async (threadId: string): Promise<void> => {
    await apiClient.post(`/email/threads/${threadId}/unread`);
  },

  /** Toggle star */
  toggleStar: async (threadId: string): Promise<boolean> => {
    const response = await apiClient.post(`/email/threads/${threadId}/star`);
    return response.data.is_starred;
  },

  /** Move to folder */
  move: async (threadId: string, folderId: string): Promise<void> => {
    await apiClient.post(`/email/threads/${threadId}/move`, {
      folder_id: folderId,
    });
  },

  /** Archive thread */
  archive: async (threadId: string): Promise<void> => {
    await apiClient.post(`/email/threads/${threadId}/archive`);
  },

  /** Delete thread (move to trash) */
  delete: async (threadId: string): Promise<void> => {
    await apiClient.delete(`/email/threads/${threadId}`);
  },
};

/**
 * Message Operations
 */
export const messageApi = {
  /** Get single message */
  get: async (messageId: string): Promise<EmailMessage> => {
    const response = await apiClient.get(`/email/messages/${messageId}`);
    return response.data;
  },

  /** Get attachment download URL */
  getAttachmentUrl: async (
    messageId: string,
    attachmentId: string
  ): Promise<string> => {
    const response = await apiClient.get(
      `/email/messages/${messageId}/attachments/${attachmentId}/url`
    );
    // Handle pending status - attachment not yet uploaded to S3
    if (response.data.status === 'pending') {
      throw new Error(response.data.message || 'Attachment is still being processed. Please try again later.');
    }
    // Support both 'url' and 'download_url' field names
    return response.data.download_url || response.data.url;
  },
};

/**
 * Send Email
 */
export const sendApi = {
  /** Send new email */
  send: async (data: SendEmailRequest): Promise<{ message_id: string }> => {
    const response = await apiClient.post('/email/send', data);
    return response.data;
  },

  /** Reply to email */
  reply: async (
    messageId: string,
    data: Omit<SendEmailRequest, 'account_id' | 'reply_to_message_id'>
  ): Promise<{ message_id: string }> => {
    const response = await apiClient.post(`/email/messages/${messageId}/reply`, data);
    return response.data;
  },

  /** Reply all */
  replyAll: async (
    messageId: string,
    data: Omit<SendEmailRequest, 'account_id' | 'reply_to_message_id'>
  ): Promise<{ message_id: string }> => {
    const response = await apiClient.post(`/email/messages/${messageId}/reply-all`, data);
    return response.data;
  },

  /** Forward email */
  forward: async (
    messageId: string,
    data: Omit<SendEmailRequest, 'account_id' | 'forward_message_id'>
  ): Promise<{ message_id: string }> => {
    const response = await apiClient.post(`/email/messages/${messageId}/forward`, data);
    return response.data;
  },
};

/**
 * Sync Operations
 */
export const syncApi = {
  /** Get sync status for account */
  getStatus: async (accountId: string): Promise<SyncStatus> => {
    const response = await apiClient.get(`/email/sync/status/${accountId}`);
    return response.data;
  },

  /** Trigger manual sync */
  trigger: async (accountId: string): Promise<void> => {
    await apiClient.post(`/email/sync/trigger/${accountId}`);
  },
};

/**
 * Header Summary (for mini-app)
 */
export const summaryApi = {
  /** Get email summary for header */
  get: async (): Promise<{
    unread_count: number;
    important_count: number;
    latest: EmailThreadPreview[];
  }> => {
    const response = await apiClient.get('/email/summary');
    return response.data;
  },
};

/**
 * AI Dashboard thread with summary
 */
export interface AIDashboardThread {
  id: string;
  subject?: string;
  sender_name?: string;
  sender_email?: string;
  last_message_at?: string;
  message_count: number;
  unread_count: number;
  has_attachments: boolean;
  is_starred: boolean;
  ai_summary?: {
    summary: string;
    one_liner?: string;
    category: string;
    priority_score: number;
    requires_response: boolean;
    sentiment?: 'positive' | 'negative' | 'neutral' | 'mixed';
    urgency?: string;
  };
  pending_action_count: number;
}

/**
 * AI Dashboard response
 */
export interface AIDashboardResponse {
  urgent: AIDashboardThread[];
  unread_with_action: AIDashboardThread[];
  unread: AIDashboardThread[];
  read_with_action: AIDashboardThread[];
  stats: {
    total_with_ai: number;
    urgent_count: number;
    unread_action_count: number;
    unread_count: number;
    read_action_count: number;
  };
}

/**
 * AI Dashboard API - Prioritized email insights
 */
export const dashboardApi = {
  /** Get AI-sorted dashboard with threads grouped by priority */
  get: async (accountId?: string, limit: number = 20): Promise<AIDashboardResponse> => {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId);
    params.append('limit', limit.toString());
    const response = await apiClient.get(`/email/ai-dashboard?${params.toString()}`);
    return response.data;
  },
};

/**
 * Thread Intelligence API - COGITO Email Intelligence System
 */
export const intelligenceApi = {
  /** Get thread intelligence (entities, action items, summary, prognosis) */
  getThreadIntelligence: async (threadId: string): Promise<ThreadIntelligence> => {
    const response = await apiClient.get(`/email/threads/${threadId}/intelligence`);
    return response.data;
  },

  /** Get related context (threads, calendar, documents, COGITO insights) */
  getRelatedContext: async (threadId: string): Promise<RelatedContext> => {
    const response = await apiClient.get(`/email/threads/${threadId}/related`);
    return response.data;
  },

  /** Get annotated message with entity highlights */
  getAnnotatedMessage: async (messageId: string): Promise<AnnotatedMessage> => {
    const response = await apiClient.get(`/email/messages/${messageId}/annotated`);
    return response.data;
  },

  /** Trigger COGITO sync for email entities */
  triggerCogitoSync: async (): Promise<{ synced_count: number; entities_created: number }> => {
    const response = await apiClient.post('/email/cogito-sync');
    return response.data;
  },

  /** Import contacts from thread participants */
  importContactsFromThread: async (threadId: string): Promise<{
    imported_count: number;
    existing_count: number;
    contacts: Array<{ id: string; email: string; name?: string }>;
  }> => {
    const response = await apiClient.post(`/email/contacts/import-from-thread/${threadId}`);
    return response.data;
  },
};

// ==================== Draft Types ====================

export interface EmailDraft {
  draft_id: string;
  account_id?: string;
  thread_id?: string;
  to?: Array<{ email: string; name?: string }>;
  cc?: Array<{ email: string; name?: string }>;
  bcc?: Array<{ email: string; name?: string }>;
  subject?: string;
  body?: string;
  body_html?: string;
  attachments?: string[];
  created_at: string;
  updated_at: string;
}

export interface SaveDraftRequest {
  draft_id?: string;
  account_id?: string;
  thread_id?: string;
  to?: Array<{ email: string; name?: string }> | string[];
  cc?: Array<{ email: string; name?: string }> | string[];
  bcc?: Array<{ email: string; name?: string }> | string[];
  subject?: string;
  body?: string;
  body_html?: string;
  attachments?: string[];
}

export interface SaveDraftResponse {
  draft_id: string;
  saved_at: string;
}

// ==================== Outbound Status Types ====================

export interface OutboundStatus {
  outbound_id: string;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  error_message?: string;
  sent_at?: string;
  retry_count: number;
  subject?: string;
}

export interface QueuedSendResponse {
  success: boolean;
  queued: boolean;
  outbound_id: string;
  status: string;
}

/**
 * Draft CRUD API - Save, list, get, delete drafts
 */
export const draftsApi = {
  /** Save or update a draft */
  save: async (draft: SaveDraftRequest): Promise<SaveDraftResponse> => {
    const response = await apiClient.post('/email/drafts', draft);
    return response.data;
  },

  /** List all drafts for the user */
  list: async (accountId?: string): Promise<EmailDraft[]> => {
    const params = accountId ? { account_id: accountId } : {};
    const response = await apiClient.get('/email/drafts', { params });
    return Array.isArray(response.data) ? response.data : [];
  },

  /** Get a specific draft by ID */
  get: async (draftId: string): Promise<EmailDraft> => {
    const response = await apiClient.get(`/email/drafts/${draftId}`);
    return response.data;
  },

  /** Delete a draft */
  delete: async (draftId: string): Promise<void> => {
    await apiClient.delete(`/email/drafts/${draftId}`);
  },
};

/**
 * Draft Improvement API - Smart Auto-Responder
 */
export const draftApi = {
  /** Get AI improvements for a draft */
  getImprovements: async (draftId: string, threadId: string): Promise<DraftImprovementResponse> => {
    const response = await apiClient.post(`/email/drafts/${draftId}/improve`, {
      thread_id: threadId,
    });
    return response.data;
  },

  /** Execute selected improvements and actions */
  executeActions: async (
    draftId: string,
    actions: ExecuteDraftActionsRequest
  ): Promise<ExecuteDraftActionsResponse> => {
    const response = await apiClient.post(`/email/drafts/${draftId}/execute`, actions);
    return response.data;
  },

  /** Preview final draft with all improvements applied */
  previewFinal: async (
    draftId: string,
    improvements: string[]
  ): Promise<{ content: string }> => {
    const response = await apiClient.post(`/email/drafts/${draftId}/preview`, {
      apply_improvements: improvements,
    });
    return response.data;
  },

  /** Update action item status */
  updateActionItem: async (
    actionItemId: string,
    status: 'pending' | 'completed' | 'dismissed'
  ): Promise<void> => {
    await apiClient.patch(`/email/action-items/${actionItemId}`, { status });
  },
};

/**
 * Outbound Status API - Check status of queued emails
 */
export const outboundApi = {
  /** Get status of a queued outbound email */
  getStatus: async (outboundId: string): Promise<OutboundStatus> => {
    const response = await apiClient.get(`/email/outbound/${outboundId}/status`);
    return response.data;
  },

  /** Poll until email is sent or failed (with timeout) */
  waitForSend: async (outboundId: string, timeoutMs: number = 60000): Promise<OutboundStatus> => {
    const startTime = Date.now();
    const pollInterval = 2000; // Poll every 2 seconds

    while (Date.now() - startTime < timeoutMs) {
      const status = await outboundApi.getStatus(outboundId);
      if (status.status === 'sent' || status.status === 'failed') {
        return status;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Timeout waiting for email to send');
  },
};

// ==================== Search Types ====================

export type SearchMode = 'keyword' | 'semantic' | 'hybrid';

export interface SearchResult {
  message_id: string;
  thread_id: string;
  subject: string;
  snippet: string;
  sender_name?: string;
  sender_email: string;
  timestamp: string;
  highlight?: string;      // Highlighted text with <mark> tags (keyword mode)
  chunk_text?: string;     // Matched chunk text (semantic mode)
  score?: number;          // Relevance score
  match_type: 'keyword' | 'semantic' | 'both';
}

export interface SearchResponse {
  results: SearchResult[];
  total_count: number;
  query: string;
  mode: SearchMode;
}

/**
 * Email Search API - Full-text, Semantic, and Hybrid Search
 *
 * Three search modes:
 * - keyword: PostgreSQL full-text search (tsvector) - fast, exact matching
 * - semantic: pgvector similarity search - meaning-based, finds conceptual matches
 * - hybrid: RRF fusion of both - best of both worlds
 */
export const searchApi = {
  /**
   * Search emails with specified mode
   */
  search: async (params: {
    query: string;
    mode?: SearchMode;
    accountId?: string;
    folderId?: string;
    limit?: number;
  }): Promise<SearchResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('q', params.query);
    if (params.mode) searchParams.append('mode', params.mode);
    if (params.accountId) searchParams.append('account_id', params.accountId);
    if (params.folderId) searchParams.append('folder_id', params.folderId);
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response = await apiClient.get(`/email/search?${searchParams.toString()}`);
    return response.data;
  },
};

// Default export with all APIs
const emailApi = {
  accounts: accountApi,
  folders: folderApi,
  threads: threadApi,
  messages: messageApi,
  send: sendApi,
  sync: syncApi,
  summary: summaryApi,
  intelligence: intelligenceApi,
  drafts: draftsApi,      // CRUD operations
  draftAi: draftApi,      // AI improvements
  outbound: outboundApi,  // Send status
  search: searchApi,      // Full-text, semantic, hybrid search
};

export default emailApi;
